/** @format */

import { createFileRoute } from "@tanstack/react-router";
import { Suspense } from "react";

import { raceQueryOptions } from "@/api/racesApi";
import { lazyWithRetry } from "@/utils/lazyWithRetry";
import PageLoader from "@/utils/PageLoader";
import { RouteErrorComponent } from "@/utils/RouteErrorComponent";
import { fetchRaceData, fetchRacesBySeriesAndYear } from "@/utils/routeUtils";

const RaceStandingsPage = lazyWithRetry(() => import("@/pages/Race/RaceStandings"));

export const Route = createFileRoute("/race/$raceId/standings")({
  /**
   * Loader prefetches data for instant rendering
   *
   * NOTE: The component also uses useRace(), useRaceStandings(), and useRacesList() hooks.
   * This is INTENTIONAL and follows the recommended TanStack Router + React Query pattern:
   *
   * - Loader prefetch = Instant render (no loading spinner)
   * - Component hooks = Reactive updates + type-safe data access
   *
   * React Query's cache deduplication prevents duplicate network requests.
   * The loader populates the cache, and hooks reactively subscribe to it.
   */
  loader: async ({ context: { queryClient }, params }) => {
    const { raceId } = params;

    // First, prefetch race data to get seriesNid and year
    // fetchRaceData now uses raceQueryOptions.detail internally
    const { seriesId, year } = await fetchRaceData(queryClient, raceId);

    // Prefetch standings and schedule in parallel
    // Uses raceQueryOptions factory for single source of truth
    await Promise.all([
      queryClient.ensureQueryData(raceQueryOptions.standings(raceId)),
      fetchRacesBySeriesAndYear(queryClient, seriesId, year),
    ]);
  },
  component: () => (
    <Suspense fallback={<PageLoader />}>
      <RaceStandingsPage />
    </Suspense>
  ),
  errorComponent: RouteErrorComponent,
});
